import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2 } from 'lucide-react';
const REDIRECT_URL = 'https://betiqpro.apuestasinteligentes.com/';
const FIVE_DAYS_MS = 5 * 24 * 60 * 60 * 1000;
export function AccessPage() {
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate();
  const handleAccess = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim()) return;
    setIsLoading(true);
    setErrorMessage('');
    await new Promise(resolve => setTimeout(resolve, 800));
    const upperCode = code.trim().toUpperCase();
    if (upperCode === 'BETIQ2026') {
      // Unlimited access
      localStorage.setItem('betiq_session', JSON.stringify({
        code: 'BETIQ2026',
        type: 'unlimited',
        authenticatedAt: new Date().toISOString()
      }));
      navigate('/betiq-dashboard');
      return;
    }
    if (upperCode === 'BETIQ5DIAS') {
      const stored = localStorage.getItem('betiq_5dias');
      if (!stored) {
        // First time using this code — activate now
        const activationDate = new Date().toISOString();
        const expirationDate = new Date(Date.now() + FIVE_DAYS_MS).toISOString();
        localStorage.setItem('betiq_5dias', JSON.stringify({
          activatedAt: activationDate,
          expiresAt: expirationDate
        }));
        localStorage.setItem('betiq_session', JSON.stringify({
          code: 'BETIQ5DIAS',
          type: '5dias',
          authenticatedAt: new Date().toISOString(),
          expiresAt: expirationDate
        }));
        navigate('/betiq-dashboard');
        return;
      }
      // Already activated — check expiration
      const data = JSON.parse(stored);
      const expiresAt = new Date(data.expiresAt).getTime();
      if (Date.now() < expiresAt) {
        // Still valid
        localStorage.setItem('betiq_session', JSON.stringify({
          code: 'BETIQ5DIAS',
          type: '5dias',
          authenticatedAt: new Date().toISOString(),
          expiresAt: data.expiresAt
        }));
        navigate('/betiq-dashboard');
        return;
      } else {
        // Expired
        const expDate = new Date(data.expiresAt).toLocaleDateString();
        setErrorMessage(`Tu acceso de 5 días expiró el ${expDate}. Contacta al administrador.`);
        setIsLoading(false);
        return;
      }
    }
    // Invalid code
    setErrorMessage('Código inválido. Verifica e intenta de nuevo.');
    setIsLoading(false);
  };
  return <div className="min-h-screen bg-[#0a0a0a] flex flex-col items-center justify-center p-6 selection:bg-blue-500/30" data-id="element-14">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.6,
      ease: [0.22, 1, 0.36, 1]
    }} className="w-full max-w-sm mx-auto" data-id="element-15">
        <div className="text-center mb-10" data-id="element-16">
          <h1 className="text-3xl font-bold text-white mb-2 tracking-tight" data-id="element-17">
            Access BetIQ
          </h1>
          <p className="text-sm text-zinc-400" data-id="element-18">
            Enter your access code to continue
          </p>
        </div>

        <form onSubmit={handleAccess} className="space-y-6" data-id="element-19">
          <div className="relative" data-id="element-20">
            <motion.div animate={errorMessage ? {
            x: [-8, 8, -8, 8, 0]
          } : {}} transition={{
            duration: 0.4
          }} data-id="element-21">
              <input type="text" value={code} onChange={e => {
              setCode(e.target.value);
              if (errorMessage) setErrorMessage('');
            }} placeholder="Enter your access code" className={`w-full bg-zinc-900/50 border ${errorMessage ? 'border-red-500/50 focus:border-red-500/50 focus:ring-red-500/20' : 'border-zinc-800 focus:border-blue-500/50 focus:ring-blue-500/20'} rounded-xl px-4 py-3.5 text-white placeholder:text-zinc-600 outline-none transition-all focus:ring-4 text-center tracking-widest uppercase`} autoComplete="off" autoCorrect="off" spellCheck="false" data-id="element-22" />
            </motion.div>

            <div className="min-h-[1.5rem] mt-2" data-id="element-23">
              <AnimatePresence data-id="element-24">
                {errorMessage && <motion.p initial={{
                opacity: 0,
                y: -5
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -5
              }} className="text-red-400 text-xs text-center" data-id="element-25">
                    {errorMessage}
                  </motion.p>}
              </AnimatePresence>
            </div>
          </div>

          <div data-id="element-26">
            <button type="submit" disabled={isLoading || !code.trim()} className="relative w-full group overflow-hidden rounded-xl bg-blue-600 px-4 py-3.5 text-sm font-semibold text-white transition-all hover:bg-blue-500 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100 shadow-[0_0_20px_rgba(37,99,235,0.15)] hover:shadow-[0_0_25px_rgba(37,99,235,0.3)]" data-id="element-27">
              {isLoading ? <Loader2 className="animate-spin mx-auto w-5 h-5" data-id="element-28" /> : 'Access Now'}
            </button>
          </div>
        </form>

        <p className="text-xs text-zinc-600 mt-8 text-center font-medium" data-id="element-29">
          Exclusive access for verified users only
        </p>
      </motion.div>
    </div>;
}